// Gambio-Kategorien-Editor - Master-Detail-Ansicht im Einstellungsbereich.
// Links: Kategoriebaum (klickbar) + Sync-Button/Status + „Neue Kategorie".
// Rechts: Detailformular der gewählten Kategorie mit Speichern/Löschen. Reihenfolge bewusst
// „Struktur zuerst": Einordnung/übergeordnete Kategorie, Aktiv, Sortierung stehen OBEN, erst
// danach die mehrsprachigen Texte (Sprach-Tabs), Bild, Anzeige-Optionen und Sitemap.
// Backend: Edge-Function `gambio-category-admin` (siehe callCategoryAdmin / rawCategoryDelete).

import { showToast } from '../../lib/toast.js';
import { infoBadge } from '../../lib/info-badge.js';
import { awaitJob } from '../../lib/await-job.js';
import {
  type SettingsCtx, DEFAULT_SHOP, callCategoryAdmin, enqueueCategorySync, fnUrl, loadShopPublicCreds,
} from './data.js';
import {
  buildCategoryTree, descendantIds, missingLanguages,
  type FlatCategory, type CategoryTreeNode, type MultiLang,
} from '../../lib/category-logic.js';

const esc = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

interface MlFieldDef { key: string; label: string; kind: 'input' | 'textarea' | 'html' }
const ML_FIELDS: MlFieldDef[] = [
  { key: 'name', label: 'Name', kind: 'input' },
  { key: 'headingTitle', label: 'Überschrift', kind: 'input' },
  { key: 'description', label: 'Beschreibung', kind: 'html' },
  { key: 'descriptionBottom', label: 'Beschreibung (unten)', kind: 'html' },
  { key: 'metaTitle', label: 'Meta-Titel', kind: 'input' },
  { key: 'metaDescription', label: 'Meta-Beschreibung', kind: 'textarea' },
  { key: 'metaKeywords', label: 'Meta-Keywords', kind: 'input' },
  { key: 'urlKeywords', label: 'URL Keywords', kind: 'input' },
  { key: 'imageAltText', label: 'Bild-Alt-Text', kind: 'input' },
];
const HTML_FIELD_KEYS = new Set(ML_FIELDS.filter((f) => f.kind === 'html').map((f) => f.key));

interface CategoryStatusRow { shop_key: string; vario_count: number | null; last_synced_at: string | null }

/** Liest ein mehrsprachiges Feld aus dem rohen Gambio-Kategorieobjekt als {lang:value}. */
function mlValue(raw: Record<string, unknown> | null, key: string): MultiLang {
  const v = raw?.[key];
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const out: MultiLang = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k] = val == null ? '' : String(val);
    return out;
  }
  if (typeof v === 'string') return { de: v };
  return {};
}

// Beschriftungen der Anzeige-Schalter 1:1 aus dem Gambio-Backend (statt generierter Feldnamen).
// showQuantityInfo = „Lagerbestand anzeigen" (verifiziert über gambio-product-display-settings),
// showQuantity = „Mengeneingabefeld anzeigen". Reihenfolge wie im Gambio-Kategorie-Backend.
const SETTING_LABELS: Record<string, string> = {
  showQuantityInfo: 'Lagerbestand anzeigen',
  showAttributes: 'Artikelattribute anzeigen',
  showGraduatedPrice: 'Staffelpreise anzeigen',
  showQuantity: 'Mengeneingabefeld anzeigen',
  showSubCategoryProducts: 'Artikel aus Unterkategorien anzeigen',
  isViewModeTiled: 'Gekachelte Artikelauflistung',
  showSubCategories: 'Unterkategorien anzeigen',
  showSubCategoryImages: 'Bild der Unterkategorien anzeigen',
  showSubCategoryNames: 'Überschrift der Unterkategorien anzeigen',
  showCategoriesImageInDescription: 'Kategoriebild in Beschreibung anzeigen',
};
const SETTING_ORDER = Object.keys(SETTING_LABELS);

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(file);
  });
}

/** Direkter Delete-Call, der den 409-Body (HAS_DEPENDENTS + Meldung) lesbar macht.
 *  `post`/`callCategoryAdmin` würden nur „HTTP 409" ohne Code/Meldung werfen. */
async function rawCategoryDelete(
  ctx: SettingsCtx, categoryId: number,
): Promise<{ ok: true } | { ok: false; code: string | null; message: string }> {
  const res = await fetch(fnUrl(ctx, 'gambio-category-admin'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${ctx.session.token}` },
    body: JSON.stringify({ shopKey: DEFAULT_SHOP, action: 'delete', categoryId }),
  });
  const jsonBody = await res.json().catch(() => ({})) as { ok?: boolean; error?: { code?: string; message?: string } };
  if (res.ok && jsonBody.ok) return { ok: true };
  return { ok: false, code: jsonBody.error?.code ?? null, message: jsonBody.error?.message ?? `HTTP ${res.status}` };
}

export async function renderCategoriesEditor(body: HTMLElement, sctx: SettingsCtx): Promise<void> {
  body.innerHTML = `<div style="display:flex;gap:18px;align-items:flex-start;">
    <div id="cat-left" style="flex:0 0 340px;max-width:340px;min-width:0;"></div>
    <div id="cat-right" style="flex:1;min-width:0;"></div>
  </div>`;
  const left = body.querySelector<HTMLElement>('#cat-left')!;
  const right = body.querySelector<HTMLElement>('#cat-right')!;

  // ── Zustand ─────────────────────────────────────────────────────────────
  let flat: FlatCategory[] = [];
  let langs: string[] = ['de'];
  let statusRow: CategoryStatusRow | null = null;
  let baseUrl: string | null = null;
  let selectedId: number | null = null;
  let mode: 'view' | 'create' = 'view';
  let createParentId = 0;
  // Kategorie, die beim Klick auf „Neue Kategorie" links markiert war. Basis für die
  // Einordnungs-Buttons („Unterkategorie von X" / „Gleiche Ebene wie X").
  let createAnchorId: number | null = null;
  let detail: Record<string, unknown> | null = null;
  let currentSettingsSrc: Record<string, unknown> | null = null;
  let initialSnapshot = '';

  // ── Rechte Spalte: Nachrichtenzeile (rot für Fehler, amber für Hinweise) ──
  function findMsg(): HTMLElement | null { return right.querySelector<HTMLElement>('#cat-msg'); }
  function showError(text: string): void {
    const el = findMsg(); if (!el) return;
    el.textContent = text;
    el.style.cssText = 'display:block;margin-top:14px;font-size:12.5px;border-radius:9px;padding:10px 13px;background:#FDECEA;color:#C0392B;border:1px solid #F5C6C0;';
  }
  function showNotice(text: string): void {
    const el = findMsg(); if (!el) return;
    el.textContent = text;
    el.style.cssText = 'display:block;margin-top:14px;font-size:12.5px;border-radius:9px;padding:10px 13px;background:#FDF1DC;color:#9A6A12;border:1px solid #F5DFB0;';
  }
  function hideMsg(): void { const el = findMsg(); if (el) el.style.display = 'none'; }

  // ── Sammelfunktionen (DOM → Werte) ────────────────────────────────────────
  function collectMl(key: string): MultiLang {
    const out: MultiLang = {};
    const isHtml = HTML_FIELD_KEYS.has(key);
    for (const l of langs) {
      if (isHtml) {
        const wrap = right.querySelector<HTMLElement>(`.cat-rte[data-htmlwrap="${key}"][data-lang="${l}"]`);
        if (!wrap) { out[l] = ''; continue; }
        // Im Quelltext-Modus (src-mode) ist die Textarea führend, sonst die WYSIWYG-Ansicht.
        if (wrap.classList.contains('src-mode')) {
          out[l] = wrap.querySelector<HTMLTextAreaElement>('textarea[data-src]')?.value ?? '';
        } else {
          out[l] = wrap.querySelector<HTMLElement>('.cat-rte-ed')?.innerHTML ?? '';
        }
        continue;
      }
      const el = right.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-ml="${key}"][data-lang="${l}"]`);
      out[l] = el ? el.value : '';
    }
    return out;
  }
  function collectFields(settingsSrc: Record<string, unknown> | null): Record<string, unknown> {
    const fields: Record<string, unknown> = {};
    for (const f of ML_FIELDS) fields[f.key] = collectMl(f.key);
    const activeBtn = right.querySelector<HTMLButtonElement>('#cat-active');
    fields['isActive'] = activeBtn ? activeBtn.classList.contains('on') : true;
    const sortEl = right.querySelector<HTMLInputElement>('#cat-sort');
    fields['sortOrder'] = sortEl ? (Number(sortEl.value) || 0) : 0;
    if (settingsSrc) {
      const merged: Record<string, unknown> = { ...settingsSrc };
      right.querySelectorAll<HTMLButtonElement>('[data-setting]').forEach((btn) => {
        merged[btn.dataset['setting']!] = btn.classList.contains('on');
      });
      right.querySelectorAll<HTMLSelectElement>('[data-sitemap]').forEach((sel) => {
        merged[sel.dataset['sitemap']!] = sel.value;
      });
      fields['settings'] = merged;
    }
    return fields;
  }

  // ── Dropdown „Übergeordnete Kategorie" (= Verschieben) ────────────────────
  function parentOptions(currentParent: number, excludeIds: Set<number>): string {
    const tree = buildCategoryTree(flat);
    let html = `<option value="0"${currentParent === 0 ? ' selected' : ''}>— Basis (keine übergeordnete) —</option>`;
    const walk = (nodes: CategoryTreeNode[], depth: number): void => {
      for (const n of nodes) {
        if (!excludeIds.has(n.gambioId)) {
          const indent = '   '.repeat(depth);
          html += `<option value="${n.gambioId}"${n.gambioId === currentParent ? ' selected' : ''}>${esc(indent + n.name)}</option>`;
        }
        if (n.children.length) walk(n.children, depth + 1);
      }
    };
    walk(tree, 0);
    return html;
  }

  // ── Einordnung (nur Anlegen-Modus) ────────────────────────────────────────
  // „Neue Kategorie" erbte früher stillschweigend die markierte Kategorie als Elternteil, und
  // das Eltern-Dropdown lag unter neun mehrsprachigen Textfeldern → die Ebene war praktisch
  // nicht wählbar (User-Befund 2026-07-27: keine Geschwister-Kategorie anlegbar). Die Einordnung
  // steht deshalb jetzt als ERSTER Block im Formular, mit Direkt-Buttons für Kind/Geschwister/
  // oberste Ebene und einer Pfadvorschau des Ziels. Aktiv + Sortierung folgen direkt darunter,
  // ebenfalls vor den Texten (gleiche User-Rückmeldung).

  function parentIdOf(id: number): number {
    return flat.find((c) => c.gambioId === id)?.parentId ?? 0;
  }

  /** Namenspfad von der Basis abwärts bis zur Kategorie, z. B. ['Möbel', 'Stühle']. */
  function categoryPath(id: number): string[] {
    const byId = new Map(flat.map((c) => [c.gambioId, c]));
    const out: string[] = [];
    const seen = new Set<number>();
    let cur = byId.get(id);
    while (cur && !seen.has(cur.gambioId)) {
      seen.add(cur.gambioId);
      out.unshift(cur.name);
      cur = cur.parentId ? byId.get(cur.parentId) : undefined;
    }
    return out;
  }

  /** „Basis › Möbel › Stühle" — Zielpfad, unter dem die neue Kategorie entsteht. */
  function placementPathLabel(parentId: number): string {
    return ['Basis', ...(parentId === 0 ? [] : categoryPath(parentId))].join(' › ');
  }

  function placementHtml(): string {
    const anchor = createAnchorId == null ? null : (flat.find((c) => c.gambioId === createAnchorId) ?? null);
    const btn = (place: string, target: number, label: string): string =>
      `<button type="button" class="btn btn-sm" data-place="${place}" data-target="${target}">${esc(label)}</button>`;
    const anchorParent = anchor ? parentIdOf(anchor.gambioId) : 0;
    // „Oberste Ebene" nur zeigen, wenn es nicht schon dasselbe Ziel wie „Gleiche Ebene" ist
    // (Anker auf Top-Level) — sonst stünden zwei Buttons für dieselbe Einordnung da.
    const quick = [
      ...(anchor
        ? [
          btn('child', anchor.gambioId, `Unterkategorie von „${anchor.name}"`),
          btn('sibling', anchorParent, `Gleiche Ebene wie „${anchor.name}"`),
        ]
        : []),
      ...(anchor && anchorParent === 0 ? [] : [btn('root', 0, 'Oberste Ebene')]),
    ].join('');
    return `<div class="sec-lab" style="margin-top:0;">Einordnung</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:10px;">${quick}</div>
      <div class="frow"><div class="field full"><label>Übergeordnete Kategorie</label>
        <div class="selwrap"><select class="sel" id="cat-parent">${parentOptions(createParentId, new Set())}</select></div></div></div>
      <div class="hint" id="cat-path" style="margin-top:-4px;"></div>`;
  }

  function settingsTogglesHtml(settings: Record<string, unknown>): string {
    // Nur die bekannten Anzeige-Schalter in Gambio-Reihenfolge mit den echten Backend-Labels.
    // Filter-Schalter (showCategoryFilter) hat einen eigenen Abschnitt; alle anderen, hier nicht
    // gezeigten Settings (z. B. onSitemap) werden beim Speichern per Round-Trip unverändert erhalten.
    const rows = SETTING_ORDER
      .filter((k) => typeof settings[k] === 'boolean')
      .map((k) => `
      <div class="toggle-card"><div class="tc-body"><div class="tc-t">${esc(SETTING_LABELS[k]!)}</div></div>
        <button type="button" class="sw ${settings[k] ? 'on' : ''}" data-setting="${esc(k)}"></button></div>`)
      .join('');
    return rows
      ? `<div class="sec-lab">Anzeige-Optionen</div>
         <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px 16px;align-items:start;">${rows}</div>`
      : '';
  }

  // Sitemap-Abschnitt der Gambio-Kategorie: onSitemap (Schalter), sitemapPriority (0.0-1.0),
  // sitemapChangeFrequency (XML-Sitemap-Standardwerte). Die Werte liegen in `settings`.
  function sitemapHtml(settings: Record<string, unknown>): string {
    const on = settings['onSitemap'] === true;
    const prio = settings['sitemapPriority'] == null ? '' : String(settings['sitemapPriority']);
    const freq = settings['sitemapChangeFrequency'] == null ? '' : String(settings['sitemapChangeFrequency']);
    const prios = ['1.0', '0.9', '0.8', '0.7', '0.6', '0.5', '0.4', '0.3', '0.2', '0.1', '0.0'];
    const prioOpts = `<option value=""${prio === '' ? ' selected' : ''}>—</option>`
      + prios.map((v) => `<option value="${v}"${v === prio ? ' selected' : ''}>${v}</option>`).join('');
    const freqs: Array<[string, string]> = [
      ['always', 'Immer'], ['hourly', 'Stündlich'], ['daily', 'Täglich'], ['weekly', 'Wöchentlich'],
      ['monthly', 'Monatlich'], ['yearly', 'Jährlich'], ['never', 'Nie'],
    ];
    const freqOpts = `<option value=""${freq === '' ? ' selected' : ''}>—</option>`
      + freqs.map(([v, l]) => `<option value="${v}"${v === freq ? ' selected' : ''}>${esc(l)}</option>`).join('');
    return `<div class="sec-lab">Sitemap</div>
      <div class="toggle-card"><div class="tc-body"><div class="tc-t">In Sitemap aufnehmen</div>
        <div class="tc-s">Kategorie in die XML-Sitemap des Shops aufnehmen.</div></div>
        <button type="button" class="sw ${on ? 'on' : ''}" data-setting="onSitemap"></button></div>
      <div class="frow">
        <div class="field"><label>Priorität</label><div class="selwrap"><select class="sel" data-sitemap="sitemapPriority">${prioOpts}</select></div></div>
        <div class="field"><label>Änderungshäufigkeit</label><div class="selwrap"><select class="sel" data-sitemap="sitemapChangeFrequency">${freqOpts}</select></div></div>
      </div>`;
  }

  function imageBoxHtml(d: Record<string, unknown>): string {
    const image = typeof d['image'] === 'string' ? d['image'] as string : '';
    const previewImg = image && baseUrl
      ? `<img id="cat-img-preview" src="${esc(baseUrl.replace(/\/$/, ''))}/images/categories/${esc(image)}" alt="" style="max-height:90px;max-width:170px;border-radius:8px;border:1px solid #E2E2E6;object-fit:contain;" />`
      : '';
    const chip = image
      ? `<span class="chip ok"><span class="d"></span>${esc(image)}</span>`
      : `<span class="chip miss"><span class="d"></span>kein Bild</span>`;
    const removeBtn = image
      ? `<button type="button" class="v-button v-button--danger" id="cat-img-remove"><span class="v-icon fa-regular fa-trash"></span> Entfernen</button>`
      : '';
    return `<div class="sec-lab">Kategorie-Bild</div>
      <div style="display:flex;align-items:center;gap:14px;flex-wrap:wrap;">
        ${previewImg}
        <div style="display:flex;flex-direction:column;gap:8px;">
          <div>${chip}</div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" id="cat-img-file" style="display:none;" />
            <button type="button" class="v-button" id="cat-img-pick"><span class="v-icon fa-regular fa-image"></span> Bild wählen…</button>
            ${removeBtn}
          </div>
        </div>
      </div>`;
  }

  // ── Rechte Spalte rendern ─────────────────────────────────────────────────
  function renderRightPlaceholder(): void {
    right.innerHTML = `<div class="hint" style="padding:24px 4px;">Wähle links eine Kategorie zum Bearbeiten oder lege mit „Neue Kategorie" eine neue an.</div>`;
  }

  function fieldHtml(f: MlFieldDef, lang: string, val: string): string {
    const req = f.key === 'name' ? ' <span class="req">*</span>' : '';
    if (f.kind === 'html') {
      const tb = (cmd: string, arg: string, label: string, title: string, style = ''): string =>
        `<button type="button" class="btn btn-ghost btn-sm" data-cmd="${cmd}"${arg ? ` data-arg="${esc(arg)}"` : ''} title="${esc(title)}" style="padding:3px 8px;${style}">${label}</button>`;
      const toolbar = [
        tb('bold', '', 'B', 'Fett', 'font-weight:700;'),
        tb('italic', '', 'I', 'Kursiv', 'font-style:italic;'),
        tb('underline', '', 'U', 'Unterstrichen', 'text-decoration:underline;'),
        tb('formatBlock', 'h2', 'H2', 'Überschrift'),
        tb('insertUnorderedList', '', '• Liste', 'Aufzählung'),
        tb('insertOrderedList', '', '1. Liste', 'Nummerierung'),
        tb('__link', '', '🔗 Link', 'Link einfügen'),
        tb('__clear', '', 'Format ✕', 'Formatierung zurücksetzen (inkl. Überschrift/Link)'),
      ].join('');
      // contenteditable enthält rohes HTML (WYSIWYG-Ansicht), die Quelltext-Textarea die escapte Version.
      return `<div class="frow"><div class="field full"><label>${esc(f.label)}${req}</label>
        <div class="cat-rte" data-htmlwrap="${esc(f.key)}" data-lang="${esc(lang)}" style="border:1px solid #d9d9de;border-radius:9px;overflow:hidden;background:#fff;">
          <div class="cat-rte-tb" style="display:flex;flex-wrap:wrap;gap:3px;align-items:center;padding:5px 6px;border-bottom:1px solid #EEEEF0;background:#FAFAFB;">
            ${toolbar}
            <button type="button" class="btn btn-ghost btn-sm" data-htmltoggle title="HTML-Quelltext anzeigen" style="margin-left:auto;padding:3px 8px;font-family:monospace;">&lt;/&gt;</button>
          </div>
          <div class="cat-rte-linkrow" style="display:none;gap:6px;align-items:center;padding:6px;border-bottom:1px solid #EEEEF0;">
            <input class="inp" data-linkinput type="url" placeholder="https://…" style="flex:1;min-width:0;height:32px;padding:4px 10px;" />
            <button type="button" class="btn btn-primary btn-sm" data-linkapply>Einfügen</button>
            <button type="button" class="btn btn-ghost btn-sm" data-linkcancel>Abbrechen</button>
          </div>
          <div class="cat-rte-ed" contenteditable="true" style="min-height:120px;padding:10px 13px;font-size:13.5px;line-height:1.55;outline:none;">${val}</div>
          <textarea class="inp" data-src style="display:none;width:100%;box-sizing:border-box;min-height:130px;border:0;border-radius:0;padding:10px 13px;resize:vertical;font-family:monospace;font-size:12px;">${esc(val)}</textarea>
        </div></div></div>`;
    }
    const control = f.kind === 'textarea'
      ? `<textarea class="inp" data-ml="${esc(f.key)}" data-lang="${esc(lang)}" style="height:auto;min-height:88px;padding:10px 13px;resize:vertical;">${esc(val)}</textarea>`
      : `<input class="inp" data-ml="${esc(f.key)}" data-lang="${esc(lang)}" value="${esc(val)}" />`;
    return `<div class="frow"><div class="field full"><label>${esc(f.label)}${req}</label>${control}</div></div>`;
  }

  function renderDetailForm(isCreate: boolean): void {
    const d: Record<string, unknown> = isCreate
      ? { parentId: createParentId, isActive: true, sortOrder: 0, name: {}, settings: {} }
      : (detail ?? {});
    const active = isCreate ? true : d['isActive'] !== false;
    const sortOrder = Number(d['sortOrder'] ?? 0) || 0;
    const parentId = Number(d['parentId'] ?? 0) || 0;
    const excludeIds = isCreate || selectedId == null
      ? new Set<number>()
      : new Set<number>([selectedId, ...descendantIds(flat, selectedId)]);
    const settingsSrc = !isCreate && d['settings'] && typeof d['settings'] === 'object'
      ? d['settings'] as Record<string, unknown>
      : null;
    currentSettingsSrc = settingsSrc;

    const langTabs = `<div style="display:flex;gap:6px;margin-bottom:12px;flex-wrap:wrap;">
      ${langs.map((l, i) => `<button type="button" class="btn btn-sm ${i === 0 ? 'btn-primary' : 'btn-ghost'}" data-langtab="${esc(l)}">${esc(l.toUpperCase())}</button>`).join('')}
    </div>`;
    const langPanels = langs.map((l, i) => `<div data-langpanel="${esc(l)}" style="${i === 0 ? '' : 'display:none;'}">
      ${ML_FIELDS.map((f) => fieldHtml(f, l, mlValue(d, f.key)[l] ?? '')).join('')}
    </div>`).join('');

    const saveLabel = isCreate ? 'Anlegen' : 'Speichern';
    const deleteBtn = isCreate ? '' : `<button type="button" class="v-button v-button--danger" id="cat-delete" style="margin-right:auto;"><span class="v-icon fa-regular fa-trash"></span> Löschen</button>`;

    right.innerHTML = `
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px;">
        <div class="sec-lab" style="margin:0;">${isCreate ? 'Neue Kategorie' : 'Kategorie bearbeiten'} ${infoBadge(isCreate
          ? 'Die Einordnung bestimmt, auf welcher Ebene die Kategorie in Gambio entsteht. Nach dem Anlegen wird sie in den Vario-Kanal gespiegelt.'
          : 'Änderungen werden nach dem Speichern in den Vario-Kanal gespiegelt.')}</div>
      </div>
      ${isCreate ? placementHtml() : ''}
      <div class="sec-lab"${isCreate ? '' : ' style="margin-top:0;"'}>Eigenschaften</div>
      <div class="toggle-card"><div class="tc-body"><div class="tc-t">Aktiv</div><div class="tc-s">Kategorie im Shop sichtbar.</div></div>
        <button type="button" class="sw ${active ? 'on' : ''}" id="cat-active"></button></div>
      <div class="frow">
        <div class="field"><label>Sortierung</label><input class="inp" id="cat-sort" type="number" step="1" value="${sortOrder}" /></div>
        ${isCreate ? '' : `<div class="field"><label>Übergeordnete Kategorie</label><div class="selwrap"><select class="sel" id="cat-parent">${parentOptions(parentId, excludeIds)}</select></div></div>`}
      </div>
      ${isCreate ? '' : settingsTogglesHtml(settingsSrc ?? {})}
      <div class="sec-lab">Texte (mehrsprachig)</div>
      ${langTabs}
      ${langPanels}
      ${isCreate ? '<div class="hint" style="margin-top:-6px;">Bild und Anzeige-Optionen sind nach dem Anlegen verfügbar.</div>' : imageBoxHtml(d)}
      ${isCreate ? '' : sitemapHtml(settingsSrc ?? {})}
      <div id="cat-msg" style="display:none;"></div>
      <div class="actions" style="margin-top:22px;display:flex;align-items:center;gap:12px;">
        ${deleteBtn}
        <button type="button" class="v-button v-button--save" id="cat-save" style="margin-left:auto;">${saveLabel}</button>
      </div>`;

    wireDetail(isCreate);
    initialSnapshot = JSON.stringify(collectFields(currentSettingsSrc));
  }

  function wireDetail(isCreate: boolean): void {
    // ── Einordnung (nur Anlegen): Direkt-Buttons setzen das Eltern-Dropdown, die
    // Pfadvorschau zeigt jederzeit das echte Ziel. Der aktive Button wird markiert.
    if (isCreate) {
      const parentSel = right.querySelector<HTMLSelectElement>('#cat-parent');
      const pathEl = right.querySelector<HTMLElement>('#cat-path');
      const refreshPlacement = (): void => {
        const cur = Number(parentSel?.value ?? 0) || 0;
        if (pathEl) pathEl.textContent = `Wird angelegt unter: ${placementPathLabel(cur)}`;
        right.querySelectorAll<HTMLButtonElement>('[data-place]').forEach((b) => {
          b.classList.toggle('btn-primary', Number(b.dataset['target']) === cur);
        });
      };
      right.querySelectorAll<HTMLButtonElement>('[data-place]').forEach((b) => {
        b.onclick = () => {
          if (parentSel) parentSel.value = b.dataset['target'] ?? '0';
          refreshPlacement();
        };
      });
      parentSel?.addEventListener('change', refreshPlacement);
      refreshPlacement();
    }
    // Sprach-Tabs
    right.querySelectorAll<HTMLButtonElement>('[data-langtab]').forEach((tab) => {
      tab.onclick = () => {
        const l = tab.dataset['langtab']!;
        right.querySelectorAll<HTMLButtonElement>('[data-langtab]').forEach((t) => {
          const on = t === tab;
          t.classList.toggle('btn-primary', on);
          t.classList.toggle('btn-ghost', !on);
        });
        right.querySelectorAll<HTMLElement>('[data-langpanel]').forEach((p) => {
          p.style.display = p.dataset['langpanel'] === l ? '' : 'none';
        });
      };
    });
    // ── Rich-Text-Editoren (Beschreibung / Beschreibung unten) ────────────────
    right.querySelectorAll<HTMLElement>('.cat-rte').forEach((wrap) => {
      const ed = wrap.querySelector<HTMLElement>('.cat-rte-ed');
      const src = wrap.querySelector<HTMLTextAreaElement>('textarea[data-src]');
      const tb = wrap.querySelector<HTMLElement>('.cat-rte-tb');
      if (!ed || !src || !tb) return;
      let savedRange: Range | null = null;
      const saveSel = (): void => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount && ed.contains(sel.anchorNode)) savedRange = sel.getRangeAt(0).cloneRange();
      };
      const restoreSel = (): void => {
        if (!savedRange) return;
        const sel = window.getSelection();
        sel?.removeAllRanges(); sel?.addRange(savedRange);
      };
      ed.addEventListener('keyup', saveSel);
      ed.addEventListener('mouseup', saveSel);
      ed.addEventListener('blur', saveSel);

      const linkRow = wrap.querySelector<HTMLElement>('.cat-rte-linkrow');
      const linkInput = wrap.querySelector<HTMLInputElement>('[data-linkinput]');
      const applyLink = (): void => {
        const url = (linkInput?.value ?? '').trim();
        if (linkRow) linkRow.style.display = 'none';
        if (!url) return;
        ed.focus(); restoreSel();
        document.execCommand('createLink', false, url);
        saveSel();
      };
      wrap.querySelector<HTMLButtonElement>('[data-linkapply]')?.addEventListener('click', applyLink);
      wrap.querySelector<HTMLButtonElement>('[data-linkcancel]')?.addEventListener('click', () => { if (linkRow) linkRow.style.display = 'none'; });

      wrap.querySelectorAll<HTMLButtonElement>('[data-cmd]').forEach((b) => {
        b.addEventListener('mousedown', (e) => e.preventDefault()); // Selektion im Editor halten
        b.addEventListener('click', () => {
          if (wrap.classList.contains('src-mode')) return; // im Quelltext-Modus keine WYSIWYG-Formatierung
          const cmd = b.dataset['cmd']!;
          if (cmd === '__link') {
            if (linkRow) linkRow.style.display = 'flex';
            if (linkInput) { linkInput.value = ''; linkInput.focus(); }
            return;
          }
          ed.focus(); restoreSel();
          if (cmd === '__clear') {
            // Vollständig zurücksetzen: Inline-Formatierung (fett/kursiv/…), Links UND
            // Block-Formatierung (z. B. H2, das fett rendert) auf normalen Absatz zurückführen.
            document.execCommand('removeFormat', false);
            document.execCommand('unlink', false);
            document.execCommand('formatBlock', false, 'p');
          } else if (cmd === 'formatBlock') {
            document.execCommand('formatBlock', false, b.dataset['arg'] || 'p');
          } else {
            document.execCommand(cmd, false);
          }
          saveSel();
        });
      });

      // HTML-Quelltext ein-/ausblenden (Textarea ↔ WYSIWYG synchron halten)
      wrap.querySelector<HTMLButtonElement>('[data-htmltoggle]')?.addEventListener('click', () => {
        const srcMode = wrap.classList.toggle('src-mode');
        if (srcMode) { src.value = ed.innerHTML; src.style.display = ''; ed.style.display = 'none'; }
        else { ed.innerHTML = src.value; src.style.display = 'none'; ed.style.display = ''; }
        tb.querySelectorAll<HTMLElement>('[data-cmd]').forEach((x) => { x.style.opacity = srcMode ? '.35' : ''; });
      });
    });

    // Toggle-Schalter
    right.querySelectorAll<HTMLButtonElement>('.sw').forEach((sw) => {
      sw.onclick = () => sw.classList.toggle('on');
    });
    // Bild-Vorschau: bei Ladefehler ausblenden (kein Inline-Handler wegen CSP)
    const img = right.querySelector<HTMLImageElement>('#cat-img-preview');
    if (img) img.onerror = () => { img.style.display = 'none'; };
    // Bild wählen / hochladen
    const pick = right.querySelector<HTMLButtonElement>('#cat-img-pick');
    const fileInput = right.querySelector<HTMLInputElement>('#cat-img-file');
    if (pick && fileInput) {
      pick.onclick = () => fileInput.click();
      fileInput.onchange = () => {
        const file = fileInput.files?.[0];
        if (!file || selectedId == null) return;
        void (async () => {
          try {
            const base64 = await fileToBase64(file);
            await callCategoryAdmin(sctx, { action: 'upload-image', categoryId: selectedId, filename: file.name, base64 });
            showToast('Bild hochgeladen.', 'success');
            await selectCategory(selectedId);
          } catch (e) { showToast(`Upload fehlgeschlagen: ${(e as Error).message}`, 'error'); }
        })();
      };
    }
    const rm = right.querySelector<HTMLButtonElement>('#cat-img-remove');
    if (rm) {
      rm.onclick = () => {
        if (selectedId == null) return;
        rm.disabled = true;
        void callCategoryAdmin(sctx, { action: 'delete-image', categoryId: selectedId })
          .then(() => { showToast('Bild entfernt.', 'success'); return selectCategory(selectedId!); })
          .catch((e: Error) => { showToast(`Fehlgeschlagen: ${e.message}`, 'error'); rm.disabled = false; });
      };
    }
    // Speichern / Anlegen
    const saveBtn = right.querySelector<HTMLButtonElement>('#cat-save')!;
    saveBtn.onclick = () => { if (isCreate) void createCategory(saveBtn); else void saveDetail(saveBtn); };
    // Löschen (zweistufig)
    const delBtn = right.querySelector<HTMLButtonElement>('#cat-delete');
    if (delBtn) delBtn.onclick = () => void deleteCategory(delBtn);
  }

  // ── Aktionen ──────────────────────────────────────────────────────────────
  async function saveDetail(btn: HTMLButtonElement): Promise<void> {
    if (selectedId == null) return;
    hideMsg();
    const nameMl = collectMl('name');
    const miss = missingLanguages(nameMl, langs);
    if (miss.length) { showError(`Name fehlt in: ${miss.map((l) => l.toUpperCase()).join(', ')}`); return; }
    const parentSel = right.querySelector<HTMLSelectElement>('#cat-parent')!;
    const newParent = Number(parentSel.value) || 0;
    const origParent = Number(detail?.['parentId'] ?? 0) || 0;
    const parentChanged = newParent !== origParent;
    const fields = collectFields(currentSettingsSrc);
    const otherChanged = JSON.stringify(fields) !== initialSnapshot;
    btn.disabled = true;
    try {
      if (parentChanged && !otherChanged) {
        await callCategoryAdmin(sctx, { action: 'move', categoryId: selectedId, newParentId: newParent });
      } else {
        const payloadFields = parentChanged ? { ...fields, parentId: newParent } : fields;
        await callCategoryAdmin(sctx, { action: 'update', categoryId: selectedId, fields: payloadFields });
      }
      await enqueueCategorySync(sctx, DEFAULT_SHOP);
      showToast('Kategorie gespeichert.', 'success');
      await loadTree();
      await selectCategory(selectedId);
    } catch (e) {
      showToast(`Speichern fehlgeschlagen: ${(e as Error).message}`, 'error');
      btn.disabled = false;
    }
  }

  async function createCategory(btn: HTMLButtonElement): Promise<void> {
    hideMsg();
    const nameMl = collectMl('name');
    const miss = missingLanguages(nameMl, langs);
    if (miss.length) { showError(`Name fehlt in: ${miss.map((l) => l.toUpperCase()).join(', ')}`); return; }
    const fields = collectFields(null); // ohne settings (Kategorie existiert noch nicht)
    const parentSel = right.querySelector<HTMLSelectElement>('#cat-parent')!;
    const parent = Number(parentSel.value) || 0;
    btn.disabled = true;
    try {
      await callCategoryAdmin(sctx, { action: 'create', parent, fields });
      await enqueueCategorySync(sctx, DEFAULT_SHOP);
      showToast('Kategorie angelegt.', 'success');
      mode = 'view';
      selectedId = null;
      detail = null;
      await loadTree();
      renderRightPlaceholder();
    } catch (e) {
      showToast(`Anlegen fehlgeschlagen: ${(e as Error).message}`, 'error');
      btn.disabled = false;
    }
  }

  async function deleteCategory(btn: HTMLButtonElement): Promise<void> {
    if (selectedId == null) return;
    hideMsg();
    // Zweistufige Bestätigung (kein window.confirm)
    if (btn.dataset['armed'] !== '1') {
      btn.dataset['armed'] = '1';
      btn.dataset['prev'] = btn.innerHTML;
      btn.innerHTML = '<span class="v-icon fa-regular fa-trash"></span> Wirklich löschen?';
      window.setTimeout(() => {
        if (btn.isConnected && btn.dataset['armed'] === '1') {
          btn.dataset['armed'] = '0';
          btn.innerHTML = btn.dataset['prev'] ?? 'Löschen';
        }
      }, 4000);
      return;
    }
    btn.dataset['armed'] = '0';
    btn.innerHTML = btn.dataset['prev'] ?? 'Löschen';
    btn.disabled = true;
    const res = await rawCategoryDelete(sctx, selectedId);
    if (!res.ok) {
      btn.disabled = false;
      if (res.code === 'HAS_DEPENDENTS') showNotice(res.message);
      else showToast(`Löschen fehlgeschlagen: ${res.message}`, 'error');
      return;
    }
    try {
      await enqueueCategorySync(sctx, DEFAULT_SHOP);
    } catch { /* Sync-Enqueue best-effort - Kategorie ist bereits gelöscht */ }
    showToast('Kategorie gelöscht.', 'success');
    selectedId = null;
    detail = null;
    await loadTree();
    renderRightPlaceholder();
  }

  // ── Linke Spalte: Baum + Status + Buttons ─────────────────────────────────
  function treeHtml(nodes: CategoryTreeNode[], depth: number): string {
    return nodes.map((n) => {
      const dotColor = n.isActive ? 'var(--vario-green)' : '#cfcfd3';
      const row = `<div class="cat-row" data-catrow="${n.gambioId}" title="${esc(n.name)}"
        style="display:flex;align-items:center;gap:8px;padding:6px 8px;padding-left:${8 + depth * 16}px;border-radius:8px;cursor:pointer;font-size:13.5px;color:var(--ink-soft);${n.isActive ? '' : 'opacity:.62;'}">
        <span style="width:7px;height:7px;border-radius:50%;background:${dotColor};flex:none;"></span>
        <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${esc(n.name)}</span></div>`;
      return row + (n.children.length ? treeHtml(n.children, depth + 1) : '');
    }).join('');
  }

  function highlightTree(): void {
    left.querySelectorAll<HTMLElement>('[data-catrow]').forEach((el) => {
      const sel = Number(el.dataset['catrow']) === selectedId;
      el.style.background = sel ? 'var(--vario-green-tint)' : '';
      el.style.color = sel ? 'var(--vario-green-dark)' : 'var(--ink-soft)';
      el.style.fontWeight = sel ? '600' : '';
    });
  }

  function renderLeft(): void {
    const count = statusRow?.vario_count ?? null;
    const last = statusRow?.last_synced_at ? new Date(statusRow.last_synced_at).toLocaleString('de-DE') : '—';
    const treeNodes = buildCategoryTree(flat);
    left.innerHTML = `
      <div class="frow">
        <div class="field"><label>In Vario</label><input class="inp" value="${esc(String(count ?? '—'))}" readonly></div>
        <div class="field"><label>Zuletzt synchron.</label><input class="inp" value="${esc(last)}" readonly></div>
      </div>
      <div style="display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap;">
        <button type="button" class="btn btn-primary btn-sm" id="cat-new"><span class="v-icon fa-regular fa-plus"></span> Neue Kategorie</button>
        <button type="button" class="btn btn-ghost btn-sm" id="cat-sync"><span class="v-icon fa-regular fa-arrows-rotate"></span> Synchronisieren</button>
      </div>
      <div id="cat-tree" style="max-height:60vh;overflow:auto;border:1px solid #EEEEF0;border-radius:11px;padding:6px;background:#FAFAFB;">
        ${treeNodes.length ? treeHtml(treeNodes, 0) : '<div class="hint" style="padding:12px;">Noch keine Kategorien synchronisiert - „Synchronisieren" startet den Abgleich.</div>'}
      </div>`;

    const tree = left.querySelector<HTMLElement>('#cat-tree')!;
    tree.addEventListener('click', (ev) => {
      const row = (ev.target as HTMLElement).closest<HTMLElement>('[data-catrow]');
      if (row) void selectCategory(Number(row.dataset['catrow']));
    });
    left.querySelector<HTMLButtonElement>('#cat-new')!.onclick = () => startCreate();
    left.querySelector<HTMLButtonElement>('#cat-sync')!.onclick = (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      // Der Abgleich lief bisher fire-and-forget: Job anstoßen, Toast, Ende. Weil ein Vario-Slot
      // sich NICHT selbst neu lädt, blieben „In Vario" und „Zuletzt synchron." auf dem alten Stand,
      // bis der Anwender die Ansicht wechselte — der Lauf sah dadurch wirkungslos aus. Jetzt wird
      // der Lauf abgewartet und danach `loadTree()` gerufen, das Baum UND Statuszeile frisch holt.
      // Wartezeit wie beim „Vollständig abgleichen" in der Produktübersicht.
      b.disabled = true;
      const label = b.innerHTML;
      b.innerHTML = '<span class="v-icon fa-regular fa-arrows-rotate"></span> Synchronisiere …';
      void (async () => {
        try {
          const jobId = await enqueueCategorySync(sctx, DEFAULT_SHOP);
          if (!jobId) {
            showToast('Kategorie-Sync konnte nicht gestartet werden.', 'error');
            return;
          }
          const job = await awaitJob(sctx.supabase, jobId, 180_000);
          await loadTree();
          if (job.status === 'completed') showToast('Kategorien abgeglichen.', 'success');
          else showToast(`Kategorie-Sync ${job.status === 'failed' ? 'fehlgeschlagen' : job.status}: ${job.error ?? 'Details im Protokoll'}`, 'error');
        } catch (err) {
          // Timeout/Netzfehler: der Job kann trotzdem durchlaufen → Ansicht dennoch nachziehen.
          await loadTree().catch(() => {});
          showToast(`Kategorie-Sync: ${(err as Error).message}`, 'error');
        } finally {
          b.disabled = false;
          b.innerHTML = label;
        }
      })();
    };
    highlightTree();
  }

  function startCreate(): void {
    createAnchorId = selectedId;
    createParentId = selectedId ?? 0;
    mode = 'create';
    selectedId = null;
    detail = null;
    highlightTree();
    renderDetailForm(true);
  }

  async function selectCategory(id: number): Promise<void> {
    selectedId = id;
    mode = 'view';
    highlightTree();
    right.innerHTML = `<div class="hint" style="padding:24px 4px;">Kategorie wird geladen …</div>`;
    try {
      const res = await callCategoryAdmin<{ ok: boolean; category: Record<string, unknown> }>(sctx, { action: 'get-detail', categoryId: id });
      detail = res.category ?? {};
      renderDetailForm(false);
    } catch (e) {
      right.innerHTML = `<div class="hint" style="padding:24px 4px;">Detail nicht ladbar: ${esc((e as Error).message)}</div>`;
    }
  }

  async function loadTree(): Promise<void> {
    const [treeRes, statusRes] = await Promise.all([
      callCategoryAdmin<{ ok: boolean; flat: FlatCategory[]; languages: string[] }>(sctx, { action: 'get-tree' }),
      sctx.supabase.rpc('gambio_get_category_status'),
    ]);
    flat = treeRes.flat ?? [];
    langs = treeRes.languages && treeRes.languages.length ? treeRes.languages : ['de'];
    const rows = (statusRes.data ?? []) as CategoryStatusRow[];
    statusRow = rows.find((r) => r.shop_key === DEFAULT_SHOP) ?? null;
    renderLeft();
  }

  // ── Initialisierung ───────────────────────────────────────────────────────
  renderRightPlaceholder();
  try { baseUrl = (await loadShopPublicCreds(sctx, DEFAULT_SHOP))?.base_url ?? null; } catch { baseUrl = null; }
  try {
    await loadTree();
  } catch (e) {
    left.innerHTML = `<div class="hint" style="padding:12px;">Kategorien nicht ladbar: ${esc((e as Error).message)}</div>`;
  }
}
